/** Which characters a chat-table PDF can actually print.
 *
 *  jsPDF's three built-in font families (helvetica / courier / times) are
 *  WinAnsi-encoded, and `pdf.ts` uses them rather than pay ~1MB to embed a
 *  Unicode TTF. Anything WinAnsi cannot express is re-encoded by jsPDF's
 *  `to8bitStream` into raw UTF-16BE — so it reaches the reader as *mojibake*,
 *  not as a missing glyph. The value is not dropped and not flagged; it is
 *  quietly wrong, which in a finance export is the worst of the three.
 *
 *  This module is the one place that knows where that edge is. It exists as a
 *  sibling of `pdf.ts` rather than inside it because it has a second caller
 *  with nothing else to do with the writer — the export menu, which warns at
 *  the moment someone chooses a format and steers them to the spreadsheet,
 *  where the whole of Unicode survives. Both callers get the same verdict from
 *  the same rule; neither has to import the other's concerns.
 *
 *  Spec: docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "PDF rendering". The decision here is *detect and warn* — deliberately NOT
 *  strip or substitute. Silently deleting or altering a value in an export is
 *  worse than rendering it badly; the whole design rests on the exported file
 *  being honest about what it contains.
 *
 *  The boundary below was not read off a cp1252 chart. It was taken from jsPDF
 *  itself — the encoding table it installs on a standard font, and the bytes it
 *  actually writes into a page's content stream — and `__tests__/pdfCharset.test.ts`
 *  re-derives both on every run, so a library bump that moves the edge fails
 *  there rather than shipping a wrong warning. */

import type { TableCell } from "./coerce";

/** The `{ header, rows }` shape `extractTable` produces. Declared structurally
 *  rather than imported from `components/Chat/Markdown/tableExtract.ts`: the
 *  components depend on `lib/export`, never the reverse. */
export interface TableText {
  header: TableCell[];
  rows: TableCell[][];
}

/** The 27 code points above U+00FF that jsPDF re-encodes into WinAnsi's
 *  0x80–0x9F block — the only part of Unicode past Latin-1 that survives.
 *
 *  This is jsPDF's own map, verbatim; the test asserts set equality against the
 *  table a live document carries, so drift cannot go unnoticed. It is copied
 *  here rather than read at runtime because the menu must be able to ask this
 *  question without loading jsPDF, which `pdf.ts` imports lazily on purpose. */
const WINANSI_ABOVE_LATIN1: ReadonlySet<number> = new Set([
  0x0152, 0x0153, // Œ œ
  0x0160, 0x0161, // Š š
  0x0178, // Ÿ
  0x017d, 0x017e, // Ž ž
  0x0192, // ƒ
  0x02c6, 0x02dc, // ˆ ˜
  0x2013, 0x2014, // – —
  0x2018, 0x2019, 0x201a, // ‘ ’ ‚
  0x201c, 0x201d, 0x201e, // “ ” „
  0x2020, 0x2021, 0x2022, // † ‡ •
  0x2026, // …
  0x2030, // ‰
  0x2039, 0x203a, // ‹ ›
  0x20ac, // €
  0x2122, // ™
]);

/** The footer paragraph `pdf.ts` adds when this module finds something.
 *
 *  It deliberately does NOT name the offending characters, however much more
 *  useful that would read. jsPDF decides the encoding per string: one character
 *  outside WinAnsi turns its whole string into UTF-16BE, so a warning that
 *  quoted `₹` would itself come out as mojibake — the one line on the page that
 *  has to survive would be the one that did not.
 *
 *  An HTML surface has no such constraint and may append the characters from
 *  `unrenderableCharactersInTable` to its own copy of this sentence.
 *
 *  Tone follows `provenance.ts`: plain-firm, names the actual failure mode, and
 *  says what to do about it. "Nothing was removed" is load-bearing — it tells a
 *  reader the figures are still on the page to check against, which is the
 *  reason we render them badly instead of dropping them. */
export const UNRENDERABLE_CHARACTERS_WARNING =
  "Warning: some characters on this page cannot be displayed by this PDF's " +
  "fonts and appear as unreadable text. Nothing was removed; use the Excel " +
  "export of this table, which shows them correctly.";

/** Does this one character reach the reader as itself?
 *
 *  Three ranges, and every one of them is asserted against jsPDF's own output:
 *
 *  - above U+00FF — only the 27 above survive; everything else is re-encoded
 *    to UTF-16BE and arrives as mojibake.
 *  - U+0080–U+009F — written straight through as that byte, which WinAnsi has
 *    already spent on a different glyph (U+0085 prints as `…`). A substitution,
 *    so it fails the "as itself" test even though a byte does arrive.
 *  - below U+0020, and U+007F — codes WinAnsi leaves undefined (PDF 32000-1,
 *    Annex D.2). `extractTable` collapses whitespace before this sees a cell,
 *    so these are not real table content; they are covered by the same rule
 *    rather than carved out of it. */
function renders(char: string): boolean {
  const codePoint = char.codePointAt(0) ?? 0;
  if (codePoint > 0xff) return WINANSI_ABOVE_LATIN1.has(codePoint);
  return (codePoint >= 0x20 && codePoint <= 0x7e) || codePoint >= 0xa0;
}

/** `for..of` over a string yields code points, so an astral character is one
 *  entry rather than two lone surrogates — a warning naming "\ud83d" would be
 *  nonsense. Distinct and in first-seen order, so a caller can list them. */
function collect(text: string, seen: Set<string>, found: string[]): void {
  for (const char of text) {
    if (renders(char) || seen.has(char)) continue;
    seen.add(char);
    found.push(char);
  }
}

/** The distinct characters in `text` that jsPDF's built-in fonts cannot print,
 *  in the order they first appear. Empty means the text is safe to render. */
export function unrenderableCharactersIn(text: string): string[] {
  const found: string[] = [];
  collect(text, new Set(), found);
  return found;
}

/** The same, across every cell of a table — header included.
 *
 *  `cell.href` is not scanned: `pdf.ts` draws the cell's text and nothing else
 *  (the URL is an xlsx concern), so warning about a link nobody can see would
 *  be a false positive with no possible fix. */
export function unrenderableCharactersInTable(table: TableText): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  for (const row of [table.header, ...table.rows]) {
    for (const cell of row) collect(cell.text, seen, found);
  }
  return found;
}
